import { Box, Button, Grid } from "@material-ui/core";
import { TodayOutlined as TodayOutlinedIcon } from "@material-ui/icons";
import React from "react";
import { Actions, Header, Section, Table } from "../../../../components";
import { User, useRequestContext } from "../../../../context";
import { AddUserModal } from "./add-user-modal";
import { init, reducer } from "./state";

export const UsersList: React.FC = () => {
  const [requestContextState, requestContextDispatch] = useRequestContext();
  const [state, dispatch] = React.useReducer(
    reducer,
    { users: requestContextState.values.users },
    init
  );
  const active = React.useMemo(() => {
    const { name, view } = requestContextState.step;

    return name === "user-information" && view === "users-list";
  }, [requestContextState.step]);

  // Effects

  React.useEffect(() => {
    if (
      active &&
      requestContextState.values.type === "revoke" &&
      !requestContextState.values.users.length
    ) {
      dispatch({
        type: "ADD_USER_MODAL:OPEN",
      });
    }
  }, [
    active,
    requestContextState.values.type,
    requestContextState.values.users.length,
  ]);

  React.useEffect(() => {
    dispatch({
      type: "RESET",
      users: requestContextState.values.users,
    });
  }, [requestContextState.values.users]);

  // Handlers

  function handleAddUserButtonClick(): void {
    dispatch({
      type: "ADD_USER_MODAL:OPEN",
    });
  }

  function handleAddUserModalClose(): void {
    dispatch({
      type: "ADD_USER_MODAL:CLOSE",
    });
  }

  function handleAddUserModalSubmit(user: User, email?: string): void {
    if (!email && state.users.find(({ email }) => email === user.email)) {
      dispatch({
        type: "ADD_USER_MODAL:ERROR",
        error: new Error("You cannot add duplicate users."),
      });
    } else {
      dispatch(
        email
          ? {
              type: "USER:UPDATE",
              email,
              user,
            }
          : {
              type: "USER:ADD",
              user,
            }
      );
      dispatch({
        type: "ADD_USER_MODAL:CLOSE",
      });
    }
  }

  function handleEditUserButtonClick(user: User): void {
    dispatch({
      type: "ADD_USER_MODAL:OPEN",
      user,
    });
  }

  function handleDeleteUserButtonClick(email: string): void {
    dispatch({
      type: "USER:DELETE",
      email,
    });
  }

  function handlePreviousButtonClick(): void {
    if (requestContextState.values.type === "revoke") {
      requestContextDispatch({
        type: "set-step",
        name: "type",
        view: "select-type",
      });
    } else {
      requestContextDispatch({
        type: "set-step",
        name: "user-information",
        view: "select-target",
      });
    }
  }

  function handleNextButtonClick(): void {
    requestContextDispatch({
      type: "set-users",
      users: state.users,
    });
    requestContextDispatch({
      type: "set-step",
      name: "system-details",
      view: "select-systems",
    });
  }

  // Helpers

  function isNextButtonDisabled(): boolean {
    return !state.users.length;
  }

  return (
    <Section active={active}>
      <Grid container spacing={2} component={Box} pb={2}>
        <Grid item xs={12}>
          <Header title="Add User">
            <Button variant="outlined" onClick={handleAddUserButtonClick}>
              + Add User
            </Button>
          </Header>
        </Grid>
      </Grid>
      <Grid container spacing={2} component={Box} flexGrow={1}>
        <Grid item xs={12} component={Box} display="flex" height="100%">
          <Table
            columns={[
              { label: "Email Address" },
              { label: "Name" },
              { label: "Employee ID" },
              { label: "Employee Start Date" },
              { label: "Remove", width: "80px" },
            ]}
            rowHeight={43}
          >
            {state.users.map((user, index) => (
              <Table.Row key={user.email}>
                <Table.Cell>{user.email}</Table.Cell>
                <Table.Cell>{user.name}</Table.Cell>
                <Table.Cell>{user.id}</Table.Cell>
                <Table.Cell>
                  {user.start_date ? (
                    <React.Fragment>
                      <TodayOutlinedIcon fontSize="small" color="disabled" />
                      <Box pl={1}>{user.start_date.toLocaleDateString()}</Box>
                    </React.Fragment>
                  ) : null}
                </Table.Cell>
                <Table.Cell>
                  <Table.Action
                    data={user.email}
                    onClick={handleDeleteUserButtonClick}
                    type="delete"
                  />
                </Table.Cell>
              </Table.Row>
            ))}
          </Table>
        </Grid>
      </Grid>
      <Grid container spacing={2} component={Box} pt={2}>
        <Grid item xs={12}>
          <Actions>
            <Actions.Cancel />
            <Actions.Previous onClick={handlePreviousButtonClick} />
            <Actions.Next
              disabled={isNextButtonDisabled()}
              onClick={handleNextButtonClick}
            />
          </Actions>
        </Grid>
      </Grid>
      <AddUserModal
        error={state.add_user_modal.error}
        open={state.add_user_modal.open}
        user={state.add_user_modal.user}
        onClose={handleAddUserModalClose}
        onSubmit={handleAddUserModalSubmit}
      />
    </Section>
  );
};
